import {
  type Dimensions,
  type RawRemotePhoto,
  type ThumbDimensions,
} from './PhotoBank.types'
import {
  PHOTO_PREVIEW_CANVAS_PARAM,
  PHOTO_PREVIEWS_PATH,
  type PHOTO_PREVIEW_SIZES,
} from './PhotoBank.consts'
import { type ValueOf } from 'utils/typeUtils'

const MINIMAL_THUMB_WIDTH = 250
const MAX_THUMB_WIDTH = 700

// The photo uploads buckets (mixtiles-uploads, -dev, -dev-<name>) are private: clients only ever see
// previews of their photos, never the original. The -transformed buckets are only read server-side.
const PRIVATE_PHOTO_URL_REGEX =
  /^https:\/\/mixtiles-uploads(-dev[\w-]*)?\.s3[\w.-]*\.amazonaws\.com\//

export const isPrivatePhotoUrl = (url: string): boolean =>
  PRIVATE_PHOTO_URL_REGEX.test(url)

export const getPhotoKeyFromUrl = (url: string): string =>
  decodeURIComponent(new URL(url).pathname.slice(1))

export type PhotoPreviewSize = ValueOf<typeof PHOTO_PREVIEW_SIZES>

// A stable link that works in any <img> or canvas load for the photo's owner, so a private photo's display URL is
// derived from its key
export const getPhotoKeyPreviewUrl = (
  key: string,
  size: PhotoPreviewSize
): string =>
  `${PHOTO_PREVIEWS_PATH}/${size}/${key
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`

export const getPhotoPreviewUrl = (
  originalUrl: string,
  size: PhotoPreviewSize
): string => getPhotoKeyPreviewUrl(getPhotoKeyFromUrl(originalUrl), size)

export const isPhotoPreviewUrl = (url: string): boolean =>
  url.startsWith(`${PHOTO_PREVIEWS_PATH}/`)

// A canvas or texture load needs CORS headers, which S3 omits from responses to plain <img> loads that the browser may
// have cached under the same URL, so canvas loads use their own link
export const getCanvasPhotoPreviewUrl = (previewUrl: string): string =>
  `${previewUrl}?${PHOTO_PREVIEW_CANVAS_PARAM}=1`

function getObjectKeysWithEnforcement<T extends object>(obj: {
  [K in keyof Required<T>]: true
}): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[]
}

// `pets` is only read from photos stored before `petDetection`, never written back
type PersistedRemotePhoto = Omit<RawRemotePhoto, 'pets'>

export const PERSISTED_PHOTO_BANK_FIELDS =
  getObjectKeysWithEnforcement<PersistedRemotePhoto>({
    uid: true,
    width: true,
    height: true,
    minimalResUrl: true,
    lowResUrl: true,
    highResUrl: true,
    originalUrl: true,
    hashCode: true,
    uploadedAt: true,
    uploadSrc: true,
    wasUsed: true,
    faces: true,
    petDetection: true,
    faceDetectionSuccess: true,
    smallFacesFiltered: true,
    isStatic: true,
  })

export const calculatePhotoThumbDimensions = (
  width: number,
  height: number
): ThumbDimensions => {
  const aspectRatio = width / height
  const getDimensions = (maxWidthOrHeight: number): Dimensions => {
    if (width > height) {
      const newWidth = Math.min(maxWidthOrHeight, width)
      return {
        width: newWidth,
        height: newWidth / aspectRatio,
      }
    }

    const newHeight = Math.min(maxWidthOrHeight, height)
    return {
      width: newHeight * aspectRatio,
      height: newHeight,
    }
  }

  return {
    minimal: getDimensions(MINIMAL_THUMB_WIDTH),
    low: getDimensions(MAX_THUMB_WIDTH),
    high: getDimensions(MAX_THUMB_WIDTH * 3),
  }
}
